import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LogIn } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { clearSessionCache } from '@/lib/auth/logout';

export function SignInPage() {
  const { isAuthenticated, isLoading, signIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isLogout = searchParams.get('logout');
  const reason = searchParams.get('reason');
  const [isSessionCleared, setIsSessionCleared] = useState(false);

  useEffect(() => {
    // Cognitoログアウト後のキャッシュクリアチェック
    if (isLogout === 'cognito') {
      clearSessionCache();
      setIsSessionCleared(true);
      return;
    }

    // トークン期限切れまたは無効として送られてきた場合
    if ((reason === 'token_expired' || reason === 'token_invalid') && !isSessionCleared) {
      console.log(`🔄 ${reason}のため、セッションをクリアします`);
      clearSessionCache();
      setIsSessionCleared(true);
      return;
    }

    // 通常の認証済みユーザーがサインインページにアクセスした場合
    if (isAuthenticated && !isSessionCleared && !reason) {
      console.log('✅ 有効なセッションが存在、ホームページにリダイレクト');
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, isLogout, reason, isSessionCleared]);

  const handleSignIn = async () => {
    // サインイン前にキャッシュをクリア
    clearSessionCache();
    try {
      await signIn();
    } catch (error) {
      console.error('サインインエラー:', error);
    }
  };

  // ローディング中は何も表示しない
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>認証状態を確認中...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">ログイン</CardTitle>
          <CardDescription>
            {reason === 'token_expired' && 'セッションの有効期限が切れました。'}
            {reason === 'token_invalid' && 'セッションが無効になりました。'}
            {!reason && 'Cognitoアカウントでログインしてください'}
            {reason && '再度ログインしてください。'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleSignIn} className="w-full" size="lg">
            <LogIn className="mr-2 h-5 w-5" />
            Cognitoでログイン
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
